import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useContactModal } from './ContactModalContext';
import ContactFormModal from './ContactFormModal';
import rp_swiss from '../img/rp_swiss_small.png';
import no_user from '../img/no_user.png';
import AuthSocialForm from './AuthSocialForm';
import UserPoller, { useUserPoller } from './UserPoller';

export default function Nav() {
  const { strings, setLang, lang } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const { showContact, contactRequest, openContact, closeContact } = useContactModal();
  const [showLoginAndRegistration, setshowLoginAndRegistration] = useState(false);

  const [isLogin, setIsLogin] = useState(true);

  // Close dropdown when clicking anywhere outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') {
        closeContact();
        setDropdownOpen(false);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const { user, loading: userLoading, error: userError } = useUserPoller();

  window.addEventListener('message', (event) => {   
    if (event.data === 'auth-success') {  
      console.log("Authentication successful");    
      window.location.reload();  
    }  
  });

  return (
    <header className="site-nav">
      <div className="nav-inner">
        <div className="nav-left">
          <a href="/"><img src={rp_swiss} alt={strings.nav.logo}/></a>
        </div>

        {/* Updated Navigation Section */}
        <nav className="nav-right" aria-label="Primary">
          {/* Top 3 Primary Links */}
          <NavLink to="/about" className={({ isActive }) => isActive ? 'btn-cta active' : 'btn-cta'}>
            {strings.nav.about}
          </NavLink>
          <NavLink to="/events" className={({ isActive }) => isActive ? 'btn-cta active' : 'btn-cta'}>
            {strings.nav.events}
          </NavLink>
          <NavLink to="/register" className={({ isActive }) => isActive ? 'btn-cta active' : 'btn-cta'}>
            {strings.nav.register}
          </NavLink>

          {/* More... Dropdown Container */}
          <div className="nav-dropdown-wrapper" ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button 
              type="button"
              className="btn-cta dropdown-toggle"
              aria-expanded={dropdownOpen}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              {strings.nav.more ?? 'More'} ▾
            </button>

            {dropdownOpen && (
              <div className="dropdown-content">
                <NavLink 
                  to="/register_professional" 
                  className={({ isActive }) => isActive ? 'menu-item selected' : 'menu-item'}
                  onClick={() => setDropdownOpen(false)}
                >
                  {strings.nav.register_professional}
                </NavLink>
                <NavLink 
                  to="/partners" 
                  className={({ isActive }) => isActive ? 'menu-item selected' : 'menu-item'}
                  onClick={() => setDropdownOpen(false)}
                >
                  {strings.nav.partners}
                </NavLink>
                <NavLink 
                  to="/past_events" 
                  className={({ isActive }) => isActive ? 'menu-item selected' : 'menu-item'}
                  onClick={() => setDropdownOpen(false)}
                >
                  {strings.nav.past_events}
                </NavLink>
              </div>
            )}
          </div>
        </nav>

        {/* Hamburger for mobile */}
        <button className="hamburger" aria-label={mobileOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileOpen} onClick={() => setMobileOpen(!mobileOpen)}>
          <svg viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M3 6h18M3 12h18M3 18h18" stroke="#ada8a8ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>

        {mobileOpen && (
          <div className="mobile-menu" role="dialog" aria-modal="false">
            <button className="mobile-close" onClick={() => setMobileOpen(false)} aria-label="Close menu">×</button>
            <nav className="mobile-nav">
              <NavLink to="/about" onClick={() => setMobileOpen(false)}>{strings.nav.about}</NavLink>
              <NavLink to="/partners" onClick={() => setMobileOpen(false)}>{strings.nav.partners}</NavLink>
              <NavLink to="/register" onClick={() => setMobileOpen(false)}>{strings.nav.register}</NavLink>
              <NavLink to="/register_professional" onClick={() => setMobileOpen(false)}>{strings.nav.register_professional}</NavLink>
              <NavLink to="/events" onClick={() => setMobileOpen(false)}>{strings.nav.events}</NavLink>
              <NavLink to="/past_events" onClick={() => setMobileOpen(false)}>{strings.nav.past_events}</NavLink>
            </nav>
          </div>
        )}

        <div className="nav-tools" aria-hidden={false}>
          <div className="nav-contact" aria-label="Contact links">
            <button className="icon" type="button" onClick={() => openContact()} aria-label={strings.contact.emailAria} title={strings.contact.emailAria}>
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1 .9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="#fff"/></svg>
            </button>
            <a className="icon" href={process.env.REACT_APP_TEL} target="_new" aria-label={strings.contact.phoneAria} title={strings.contact.phoneAria} >
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 01.9-.27 11.36 11.36 0 003.58.57 1 1 0 011 1v3.5a1 1 0 01-1 1A16 16 0 013 5a1 1 0 011-1h3.5a1 1 0 011 1 11.36 11.36 0 00.57 3.58 1 1 0 01-.27.9l-2.18 2.31z" fill="#fff"/></svg>
            </a>
            <a className="icon" href={strings.social_media.instagram} target="_new" aria-label={strings.contact.instagramAria} title={strings.contact.instagramAria} >
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M7 2h10a5 5 0 015 5v10a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5zm5 6.2A4.8 4.8 0 1016.8 13 4.8 4.8 0 0012 8.2zm5.2-3.4a1.12 1.12 0 11-1.12 1.12A1.12 1.12 0 0117.2 4.8zM12 10.4A1.6 1.6 0 1110.4 8.8 1.6 1.6 0 0112 10.4z" fill="#fff"/></svg>
            </a>
            <a className="icon" href={process.env.REACT_APP_WATSAPP} target="_new" aria-label={strings.contact.whatsappAria} title={strings.contact.whatsappAria}>
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M20.52 3.48A11.94 11.94 0 0012 0C5.37 0 .01 5.36.01 12a11.8 11.8 0 001.7 6l-1 4 4-1A11.94 11.94 0 0012 24c6.63 0 12-5.36 12-12 0-3.2-1.24-6.21-3.48-8.52zM12 21.5c-2.06 0-4-.57-5.7-1.56l-.4-.24-2.74.7.73-2.67-.26-.43A9.5 9.5 0 012.5 12 9.5 9.5 0 0112 2.5 9.5 9.5 0 0121.5 12 9.5 9.5 0 0112 21.5zM17.28 14.3c-.26-.13-1.54-.76-1.78-.85-.24-.09-.41-.13-.58.13s-.66.85-.8 1.03c-.14.17-.28.2-.52.07-.24-.13-1.02-.37-1.94-1.19-.72-.64-1.2-1.44-1.34-1.68-.14-.24-.02-.37.11-.5.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.58-1.4-.8-1.9-.21-.5-.43-.43-.58-.44l-.5-.01c-.17 0-.43.06-.66.3-.23.24-.88.86-.88 2.09s.9 2.42 1.02 2.59c.12.17 1.78 2.78 4.32 3.9 3.04 1.4 3.29 1.12 3.89 1.05.6-.07 1.95-.79 2.23-1.56.28-.77.28-1.43.2-1.56-.08-.13-.24-.2-.5-.33z" fill="#fff"/></svg>
            </a>
          </div>
        </div>

        <ContactFormModal
          isOpen={showContact}
          onClose={closeContact}
          subject={contactRequest?.subject}
          body={contactRequest?.body}
        />

        {showLoginAndRegistration && createPortal(
          <div className="image-modal-overlay" onClick={() => setshowLoginAndRegistration(false)}>
            <div className="image-modal-wrapper" onClick={(e) => e.stopPropagation()}>
              <div className="contact-modal" role="dialog" aria-modal="true">
                <button className="image-modal-close" onClick={() => setshowLoginAndRegistration(false)} aria-label="Close Login form">×</button> 
                {AuthSocialForm(strings)}   
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </header>
  );
}